import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { authAPI } from "../utils/api";
import {
  FiPhone,
  FiLock,
  FiUser,
  FiCheckCircle,
  FiShield,
  FiAlertCircle,
} from "react-icons/fi";

const Register = () => {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // 폼 상태 관리
  const [formData, setFormData] = useState({
    phone: "",
    code: "",
    userType: "",
    name: "",
    username: "",
    password: "",
    repassword: "",
  });

  // 추가 상태 관리
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showVerificationField, setShowVerificationField] = useState(false);
  const [error, setError] = useState("");
  const [isAgreeTerm, setIsAgreeTerm] = useState(false);

  // 입력값 변경 처리
  const handleChange = (e) => {
    const { name, value } = e.target;

    // 전화번호에서 하이픈 자동 제거
    if (name === "phone") {
      const cleanPhone = value.replace(/-/g, "");
      setFormData({ ...formData, [name]: cleanPhone });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // 카운트다운 타이머 설정
  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [countdown]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // 유효성 검사
  const isValidPhone = formData.phone.trim() !== "" && formData.phone.length >= 10;
  const isValidPassword = formData.password === formData.repassword;
  const isValid =
    isPhoneVerified &&
    formData.userType !== "" &&
    isAgreeTerm &&
    formData.name !== "" &&
    formData.username !== "" &&
    formData.password !== "" &&
    formData.repassword !== "" &&
    isValidPassword;

  // 휴대폰 인증번호 발송 요청
  const handleVerification = async (e) => {
    e.preventDefault();

    if (!isValidPhone) return;

    try {
      setVerifyLoading(true);
      setError("");

      const cleanPhone = formData.phone.replace(/-/g, "");

      // API 호출하여 인증번호 요청
      const response = await authAPI.verify(cleanPhone);

      if (response && response.success) {
        setVerificationSent(true);
        setShowVerificationField(true);
        setCountdown(180); // 3분 대기 시간 설정
        alert("인증번호가 발송되었습니다. 문자메시지를 확인해주세요.");
      } else {
        throw new Error("인증번호를 받지 못했습니다.");
      }
    } catch (err) {
      console.error("인증 요청 에러:", err);
      setError(
        err.response?.data?.message || "인증번호 발송 중 오류가 발생했습니다."
      );
      setCountdown(0);
    } finally {
      setVerifyLoading(false);
    }
  };

  // 인증번호 검증 요청
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!formData.code || formData.code.length < 6) return;

    try {
      setVerifyCodeLoading(true);
      setError("");

      const cleanPhone = formData.phone.replace(/-/g, "");
      const response = await authAPI.verifyCode(cleanPhone, formData.code);

      if (response && response.success) {
        setIsPhoneVerified(true);
        setCountdown(0);
        alert("휴대폰 인증에 성공했습니다.");
      } else {
        throw new Error(response?.message || "인증번호가 올바르지 않습니다.");
      }
    } catch (err) {
      console.error("인증 검증 에러:", err);
      setError(
        err.response?.data?.message || "인증번호 검증 중 오류가 발생했습니다."
      );
    } finally {
      setVerifyCodeLoading(false);
    }
  };

  // 회원가입 제출
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAgreeTerm) {
      setError("이용약관에 동의해주셔야 회원가입이 가능합니다.");
      return;
    }

    if (!isPhoneVerified) {
      setError("휴대폰 본인인증을 완료해주세요.");
      return;
    }

    if (!formData.userType) {
      setError("일반 회원 또는 사찰/스님 회원 중 회원 종류를 선택해주세요.");
      return;
    }

    if (!formData.name.trim()) {
      setError("이름을 입력해주세요.");
      return;
    }

    if (!formData.username.trim()) {
      setError("아이디를 입력해주세요.");
      return;
    }

    if (!formData.password) {
      setError("비밀번호를 입력해주세요.");
      return;
    }

    if (!formData.repassword) {
      setError("비밀번호 확인을 입력해주세요.");
      return;
    }

    if (!isValidPassword) {
      setError("비밀번호가 서로 일치하지 않습니다.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // API 호출
      await authAPI.signUp({
        username: formData.username,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        type: formData.userType,
        agree_term: isAgreeTerm,
        device_token: "",
      });

      alert("회원가입이 완료되었습니다. 가입한 정보로 로그인을 진행해주세요.");
      navigate("/login");
    } catch (err) {
      console.error("회원가입 에러:", err);
      setError(
        typeof err.response?.data === "string"
          ? err.response?.data
          : err.response?.data?.message || "회원가입 중 오류가 발생했습니다."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5" style={{ minHeight: "90vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        .register-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 149, 144, 0.05);
          border: 1px solid rgba(0, 149, 144, 0.08);
          transition: all 0.3s ease;
          width: 100%;
          max-width: 580px;
        }
        
        .form-input-custom {
          border-radius: 10px;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border: 1px solid #edf2f7;
          background-color: #f7fafc;
          transition: all 0.2s ease;
          font-size: 0.95rem;
        }
        
        .form-input-custom:focus {
          border-color: var(--main-color);
          background-color: #ffffff;
          box-shadow: 0 0 0 3px rgba(0, 149, 144, 0.15);
          outline: none;
        }
        
        .input-icon-wrapper {
          position: relative;
        }
        
        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          font-size: 1.1rem;
          transition: color 0.2s ease;
          z-index: 10;
        }
        
        .form-input-custom:focus + .input-icon {
          color: var(--main-color);
        }
        
        .user-type-card {
          background: #ffffff;
          border: 2px solid #edf2f7;
          border-radius: 15px;
          padding: 1.25rem;
          cursor: pointer;
          transition: all 0.25s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          height: 100%;
        }
        
        .user-type-card:hover {
          border-color: rgba(0, 149, 144, 0.4);
          transform: translateY(-2px);
        }
        
        .user-type-card.active {
          border-color: var(--main-color);
          background: rgba(0, 149, 144, 0.03);
          box-shadow: 0 5px 15px rgba(0, 149, 144, 0.08);
        }
        
        .user-type-icon {
          font-size: 2rem;
          color: #a0aec0;
          margin-bottom: 0.5rem;
          transition: all 0.25s ease;
        }
        
        .user-type-card.active .user-type-icon {
          color: var(--main-color);
          transform: scale(1.05);
        }
        
        .user-type-label {
          font-weight: 600;
          font-size: 0.95rem;
          color: #4a5568;
          transition: color 0.25s ease;
        }
        
        .user-type-card.active .user-type-label {
          color: var(--main-color);
        }
        
        .btn-gradient {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          padding: 0.75rem;
          transition: all 0.25s ease;
          box-shadow: 0 4px 15px rgba(0, 149, 144, 0.15);
        }
        
        .btn-gradient:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 149, 144, 0.25);
          background: linear-gradient(135deg, var(--main-hover) 0%, #00605c 100%);
        }
        
        .btn-gradient:disabled {
          background: #cbd5e0;
          color: #a0aec0;
          box-shadow: none;
          cursor: not-allowed;
        }
        
        .btn-outline-custom {
          border: 1px solid var(--main-color);
          color: var(--main-color);
          background: transparent;
          border-radius: 10px;
          font-weight: 600;
          padding: 0.75rem;
          transition: all 0.2s ease;
        }
        
        .btn-outline-custom:hover:not(:disabled) {
          background: var(--main-color);
          color: #ffffff;
        }

        .btn-outline-custom:disabled {
          border-color: #cbd5e0;
          color: #cbd5e0;
          cursor: not-allowed;
        }
        
        .terms-box {
          height: 140px;
          overflow-y: auto;
          font-size: 0.85rem;
          line-height: 1.6;
          color: #4a5568;
          background: #f7fafc;
          border: 1px solid #edf2f7;
          border-radius: 12px;
          padding: 1rem;
        }
        
        .terms-box::-webkit-scrollbar {
          width: 5px;
        }
        
        .terms-box::-webkit-scrollbar-thumb {
          background-color: #cbd5e0;
          border-radius: 3px;
        }
        
        .gradient-title {
          background: linear-gradient(135deg, var(--main-color) 0%, var(--main-hover) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          letter-spacing: -0.5px;
        }
        
        .form-label-custom {
          font-weight: 600;
          font-size: 0.88rem;
          color: #4a5568;
          margin-bottom: 0.5rem;
        }
      `}</style>

      <Card className="register-card shadow-lg">
        <Card.Header className="bg-white border-bottom-0 pt-5 pb-3">
          <h2 className="text-center gradient-title">비나이다 회원가입</h2>
          <p className="text-center text-muted small mb-0">안전하고 편리하게 비나이다 서비스를 시작해 보세요.</p>
        </Card.Header>

        <Card.Body className="px-4 pb-5">
          <Form onSubmit={handleSubmit}>
            {/* 이용약관 섹션 */}
            <section className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="form-label-custom">이용약관 동의</span>
                <Form.Check
                  type="checkbox"
                  id="agree-terms"
                  label={<span style={{ fontSize: "0.9rem", fontWeight: "500", color: "#4a5568" }}>약관에 동의합니다 (필수)</span>}
                  checked={isAgreeTerm}
                  onChange={(e) => setIsAgreeTerm(e.target.checked)}
                  className="mb-0"
                />
              </div>
              <div className="terms-box">
                <h6 className="fw-bold mb-2">비나이다 이용약관</h6>
                <div className="mb-2">
                  <p className="fw-bold mb-1" style={{ fontSize: "0.8rem" }}>제1조 목적</p>
                  <p className="mb-2">
                    이 약관은 주식회사 비나이다컴퍼니(이하 "회사")가 운영하는
                    비나이다(이하 "비나이다")에서 제공하는 인터넷 관련
                    서비스(이하 "서비스")를 이용함에 있어 비나이다와 이용자의
                    권리·의무 및 책임사항을 규정함을 목적으로 합니다.
                  </p>
                </div>
                <div className="mb-2">
                  <p className="fw-bold mb-1" style={{ fontSize: "0.8rem" }}>제2조 정의</p>
                  <p className="mb-2">
                    ① 비나이다는 회사가 용역 또는 재화(이하 "용역 등")를
                    이용자에게 제공하기 위하여 컴퓨터 등 정보통신설비를
                    이용하여 용역 등을 거래할 수 있도록 설정한 가상의 영업장을
                    말합니다.
                  </p>
                  <p className="mb-0">
                    ② "이용자"란 비나이다에 접속하여 이 약관에 따라 비나이다가
                    제공하는 서비스를 받는 회원 및 비회원을 말합니다.
                  </p>
                </div>
              </div>
            </section>

            {/* 휴대폰 인증 섹션 */}
            <section className="mb-4">
              <span className="form-label-custom d-block">휴대폰 본인인증</span>
              <Row className="g-2 mb-2">
                <Col xs={8}>
                  <div className="input-icon-wrapper">
                    <Form.Control
                      type="text"
                      name="phone"
                      placeholder="전화번호 입력 (- 제외)"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isPhoneVerified}
                      className="form-input-custom w-100"
                      required
                    />
                    <FiPhone className="input-icon" />
                  </div>
                </Col>
                <Col xs={4}>
                  <Button
                    onClick={handleVerification}
                    disabled={!isValidPhone || verifyLoading || countdown > 0 || isPhoneVerified}
                    className="btn-outline-custom w-100 h-100 d-flex align-items-center justify-content-center"
                  >
                    {verifyLoading ? (
                      <Spinner as="span" animation="border" size="sm" />
                    ) : countdown > 0 ? (
                      `${countdown}초`
                    ) : verificationSent ? (
                      "재요청"
                    ) : (
                      "인증 요청"
                    )}
                  </Button>
                </Col>
              </Row>

              {/* 인증번호 입력 필드는 요청 후에만 표시 */}
              {showVerificationField && !isPhoneVerified && (
                <Row className="g-2 mb-2">
                  <Col xs={8}>
                    <div className="input-icon-wrapper">
                      <Form.Control
                        type="text"
                        name="code"
                        placeholder="6자리 인증번호 입력"
                        value={formData.code}
                        onChange={handleChange}
                        maxLength={6}
                        required
                        className="form-input-custom w-100"
                      />
                      <FiLock className="input-icon" />
                    </div>
                  </Col>
                  <Col xs={4}>
                    <Button
                      onClick={handleVerifyCode}
                      disabled={!formData.code || formData.code.length < 6 || verifyCodeLoading}
                      className="btn-gradient w-100 h-100 d-flex align-items-center justify-content-center"
                    >
                      {verifyCodeLoading ? (
                        <Spinner as="span" animation="border" size="sm" />
                      ) : (
                        "인증 확인"
                      )}
                    </Button>
                  </Col>
                </Row>
              )}

              {isPhoneVerified && (
                <div className="d-flex align-items-center text-success small mt-1">
                  <FiCheckCircle className="me-1" /> 휴대폰 본인 인증이 완료되었습니다.
                </div>
              )}
            </section>

            <hr className="my-4" style={{ borderColor: "#edf2f7" }} />

            {/* 회원 종류 선택 */}
            <section className="mb-4">
              <span className="form-label-custom d-block">회원 종류 선택</span>
              <Row className="g-3">
                <Col xs={6}>
                  <div
                    onClick={() => !isPhoneVerified ? null : setFormData({ ...formData, userType: "Normal" })}
                    className={`user-type-card ${formData.userType === "Normal" ? "active" : ""} ${!isPhoneVerified ? "opacity-50" : ""}`}
                    style={{ pointerEvents: !isPhoneVerified ? "none" : "auto" }}
                  >
                    <FiUser className="user-type-icon" />
                    <span className="user-type-label">일반 회원</span>
                  </div>
                </Col>
                <Col xs={6}>
                  <div
                    onClick={() => !isPhoneVerified ? null : setFormData({ ...formData, userType: "Manager" })}
                    className={`user-type-card ${formData.userType === "Manager" ? "active" : ""} ${!isPhoneVerified ? "opacity-50" : ""}`}
                    style={{ pointerEvents: !isPhoneVerified ? "none" : "auto" }}
                  >
                    <FiShield className="user-type-icon" />
                    <span className="user-type-label">사찰 / 스님 회원</span>
                  </div>
                </Col>
              </Row>
                {!isPhoneVerified && (
                  <div className="text-muted small text-center mt-2">
                    * 회원 종류 선택 및 정보 입력은 휴대폰 본인인증 완료 후에 가능합니다.
                  </div>
                )}
              </section>

              {isPhoneVerified && (
                <>
                  <hr className="my-4" style={{ borderColor: "#edf2f7" }} />

                  {/* 회원 정보 입력 */}
                  <section className="mb-4">
                    <div className="mb-3">
                      <Form.Label className="form-label-custom">이름</Form.Label>
                      <div className="input-icon-wrapper">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="이름을 입력하세요"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-input-custom w-100"
                          required
                        />
                        <FiUser className="input-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <Form.Label className="form-label-custom">아이디</Form.Label>
                      <div className="input-icon-wrapper">
                        <Form.Control
                          type="text"
                          name="username"
                          placeholder="사용할 아이디를 입력하세요"
                          value={formData.username}
                          onChange={handleChange}
                          className="form-input-custom w-100"
                          required
                        />
                        <FiUser className="input-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <Form.Label className="form-label-custom">비밀번호</Form.Label>
                      <div className="input-icon-wrapper">
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="비밀번호를 입력하세요"
                          value={formData.password}
                          onChange={handleChange}
                          className="form-input-custom w-100"
                          required
                        />
                        <FiLock className="input-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <Form.Label className="form-label-custom">비밀번호 확인</Form.Label>
                      <div className="input-icon-wrapper">
                        <Form.Control
                          type="password"
                          name="repassword"
                          placeholder="비밀번호를 한 번 더 입력하세요"
                          value={formData.repassword}
                          onChange={handleChange}
                          className="form-input-custom w-100"
                          required
                          isInvalid={formData.repassword && !isValidPassword}
                        />
                        <FiLock className="input-icon" />
                        {formData.repassword && !isValidPassword && (
                          <Form.Control.Feedback type="invalid" style={{ paddingLeft: "2.75rem" }}>
                            비밀번호가 일치하지 않습니다.
                          </Form.Control.Feedback>
                        )}
                      </div>
                    </div>
                  </section>
                </>
              )}

            {error && (
              <Alert variant="danger" className="mb-3 d-flex align-items-center" style={{ borderRadius: "10px" }}>
                <FiAlertCircle className="me-2" style={{ fontSize: "1.2rem", flexShrink: 0 }} />
                <div style={{ fontSize: "0.9rem" }}>{error}</div>
              </Alert>
            )}

             {/* 제출 버튼 */}
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              className="btn-gradient w-100 py-3 mt-1"
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">회원가입 처리 중...</span>
                </>
              ) : (
                "가입 완료"
              )}
            </Button>

            <div className="text-center mt-4">
              <span className="text-muted small">
                이미 계정이 있으신가요?{" "}
                <Link to="/login" style={{ color: "var(--main-color)", fontWeight: "600", textDecoration: "underline" }}>
                  로그인하기
                </Link>
              </span>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Register;
